import { Box, Tab, Tabs } from '@mui/material';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { CoverageSection, EditorSection } from 'widgets/translation';

import { translationLib, translationModel } from 'entities/translation';

import { EEntityType, ETranslationStatus } from 'shared/api';

const DEFAULT_TYPE = EEntityType.Disease;

type TranslationTab = 'coverage' | 'editor';

const parseTab = (raw: string | null): TranslationTab => (raw === 'editor' ? 'editor' : 'coverage');

const parseEntityType = (raw: string | null): EEntityType => {
  if (!raw) return DEFAULT_TYPE;
  return (translationLib.ENTITY_TYPES.includes(raw as EEntityType) ? raw : DEFAULT_TYPE) as EEntityType;
};

const parseStatus = (raw: string | null): ETranslationStatus => {
  switch (raw) {
    case ETranslationStatus.Missing:
    case ETranslationStatus.Translated:
      return raw;
    default:
      return ETranslationStatus.All;
  }
};

const parseSortOrder = (raw: string | null): 'asc' | 'desc' => (raw === 'desc' ? 'desc' : 'asc');

const parsePositiveInt = (raw: string | null, fallback: number): number => {
  const parsed = parseInt(raw ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const TranslationsPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // Languages are needed by both tabs (editor select + default-language backfill),
  // so they are fetched at the page level — independent of which tab is mounted.
  translationModel.getLanguagesAsync()(dispatch);
  const { languages } = translationModel.useTranslationState();

  const tab = parseTab(searchParams.get('tab'));
  const entityType = parseEntityType(searchParams.get('type'));
  const language = searchParams.get('lang') ?? '';
  const status = parseStatus(searchParams.get('status'));
  const filter = searchParams.get('filter') ?? '';
  const sortBy = searchParams.get('sortBy') ?? 'code';
  const sortOrder = parseSortOrder(searchParams.get('sortOrder'));
  const page = parsePositiveInt(searchParams.get('page'), 1);
  const rowsPerPage = parsePositiveInt(searchParams.get('limit'), 25);

  // Coverage matrix has its own client-side sort, kept separate from the
  // editor's `sortBy`/`sortOrder` (which are sent to the server).
  const covSortBy = searchParams.get('covSort') ?? '';
  const covSortOrder = parseSortOrder(searchParams.get('covOrder'));

  const [localFilter, setLocalFilter] = useState(filter);

  useEffect(() => {
    if (!language && languages.length > 0) {
      const next = new URLSearchParams(searchParams);
      next.set('lang', languages[0].code);
      setSearchParams(next, { replace: true });
    }
  }, [language, languages, searchParams, setSearchParams]);

  useEffect(() => {
    const current = searchParams.get('filter') ?? '';
    if (localFilter === current) return;
    const next = new URLSearchParams(searchParams);
    if (localFilter) next.set('filter', localFilter);
    else next.delete('filter');
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  }, [localFilter]);

  const patchParams = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleTabChange = useCallback(
    (_: SyntheticEvent, value: TranslationTab) => patchParams({ tab: value }),
    [patchParams],
  );

  const handleEntityTypeChange = useCallback((type: EEntityType) => patchParams({ type, page: '1' }), [patchParams]);

  const handleLanguageChange = useCallback((lang: string) => patchParams({ lang, page: '1' }), [patchParams]);

  const handleStatusChange = useCallback(
    (s: ETranslationStatus) => patchParams({ status: s, page: '1' }),
    [patchParams],
  );

  const handleFilterChange = useCallback((value: string) => setLocalFilter(value), []);

  const handleSortChange = useCallback(
    (columnId: string) => {
      if (sortBy === columnId) {
        patchParams({ sortOrder: sortOrder === 'asc' ? 'desc' : 'asc', page: '1' });
      } else {
        patchParams({ sortBy: columnId, sortOrder: 'asc', page: '1' });
      }
    },
    [patchParams, sortBy, sortOrder],
  );

  const handlePageChange = useCallback((p: number) => patchParams({ page: String(p) }), [patchParams]);

  const handleRowsPerPageChange = useCallback(
    (n: number) => patchParams({ limit: String(n), page: '1' }),
    [patchParams],
  );

  const handleCovSortChange = useCallback(
    (key: string) => {
      if (covSortBy === key) {
        patchParams({ covOrder: covSortOrder === 'asc' ? 'desc' : 'asc' });
      } else {
        patchParams({ covSort: key, covOrder: 'asc' });
      }
    },
    [patchParams, covSortBy, covSortOrder],
  );

  // Drilling into a coverage cell jumps to the editor tab with that selection.
  const handleCellClick = useCallback(
    (type: EEntityType, lang: string) => patchParams({ tab: 'editor', type, lang, page: '1' }),
    [patchParams],
  );

  const handleLanguageCellClick = useCallback(
    (lang: string) => patchParams({ tab: 'editor', lang, page: '1' }),
    [patchParams],
  );

  const effectiveLanguage = useMemo(
    () => (languages.some((l) => l.code === language) ? language : ''),
    [languages, language],
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      px={3}
      py={2}
      gap={2}
      sx={{ height: 'calc(100vh - 64px)', overflow: 'hidden' }}
    >
      <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.05)', borderRadius: '8px', width: 'fit-content' }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab value="coverage" label="Coverage" />
          <Tab value="editor" label="Editor" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {tab === 'coverage' ? (
          <CoverageSection
            selectedType={entityType}
            selectedLanguage={effectiveLanguage || null}
            sortBy={covSortBy}
            sortOrder={covSortOrder}
            onSortChange={handleCovSortChange}
            onCellClick={handleCellClick}
            onLanguageClick={handleLanguageCellClick}
          />
        ) : (
          <EditorSection
            entityType={entityType}
            language={effectiveLanguage}
            status={status}
            filter={localFilter}
            sortBy={sortBy}
            sortOrder={sortOrder}
            page={page}
            rowsPerPage={rowsPerPage}
            onEntityTypeChange={handleEntityTypeChange}
            onLanguageChange={handleLanguageChange}
            onStatusChange={handleStatusChange}
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        )}
      </Box>
    </Box>
  );
};
